"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import {
    AlertTriangle,
    CheckCircle2,
    Loader2,
    Search,
    X,
} from "lucide-react";
import { useAuth } from "@/providers/auth-provider";
import { useAssistantIndexStore } from "@/stores/assistant-index-store";
import type { AssistantIndexJobView } from "@/lib/ai/assistant-index-job-types";

const POLL_INTERVAL_MS = 2500;

function isActive(job: AssistantIndexJobView): boolean {
    return job.status === "queued" || job.status === "running";
}

function progressLine(job: AssistantIndexJobView): string {
    const range = `chapters ${job.startSortOrder}–${job.endSortOrder}`;
    if (job.status === "queued") {
        return `Preparing ${range}…`;
    }
    if (job.status === "running") {
        return `${job.embeddedChapters}/${job.totalChapters} searchable in ${range}…`;
    }
    if (job.status === "succeeded") {
        return `${job.embeddedChapters}/${job.totalChapters} chapters are searchable. Retry your assistant request.`;
    }
    return (
        job.error ??
        `${job.embeddedChapters}/${job.totalChapters} chapters became searchable before indexing stopped.`
    );
}

export function AssistantIndexTray() {
    const { user } = useAuth();
    const pathname = usePathname();
    const ownerId = useAssistantIndexStore((state) => state.ownerId);
    const storedJobs = useAssistantIndexStore((state) => state.jobs);
    const setOwner = useAssistantIndexStore((state) => state.setOwner);
    const refresh = useAssistantIndexStore((state) => state.refresh);
    const dismiss = useAssistantIndexStore((state) => state.dismiss);
    const jobs = ownerId === user?.id ? storedJobs : [];

    useEffect(() => {
        const nextOwnerId = user?.id ?? null;
        setOwner(nextOwnerId);
        if (nextOwnerId) void refresh(nextOwnerId);
    }, [user?.id, pathname, refresh, setOwner]);

    // The SSE start event normally makes a new job visible immediately. A
    // navigation can abort that stream after the durable row was created, so
    // also reconcile when the author returns to the tab.
    useEffect(() => {
        if (!user) return;
        const onFocus = () => void refresh(user.id);
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [user, refresh]);

    const hasActive = jobs.some(isActive);
    useEffect(() => {
        if (!user || !hasActive) return;
        const id = setInterval(
            () => void refresh(user.id),
            POLL_INTERVAL_MS,
        );
        return () => clearInterval(id);
    }, [user, hasActive, refresh]);

    if (!user || jobs.length === 0) return null;

    return (
        <>
            {jobs.map((job) => (
                <div
                    key={job.id}
                    className="rounded-xl border border-border bg-card/95 p-3 shadow-lg backdrop-blur-md"
                >
                    <div className="flex items-start gap-2.5">
                        <div className="mt-0.5 shrink-0">
                            {job.status === "succeeded" ? (
                                <CheckCircle2
                                    className="h-4 w-4 text-primary"
                                    aria-hidden="true"
                                />
                            ) : job.status === "failed" ? (
                                <AlertTriangle
                                    className="h-4 w-4 text-destructive"
                                    aria-hidden="true"
                                />
                            ) : (
                                <Loader2
                                    className="h-4 w-4 animate-spin text-primary"
                                    aria-hidden="true"
                                />
                            )}
                        </div>

                        <div className="min-w-0 flex-1">
                            <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                                <Search
                                    className="h-3.5 w-3.5 text-primary"
                                    aria-hidden="true"
                                />
                                {job.status === "succeeded"
                                    ? "Chapter search ready"
                                    : job.status === "failed"
                                      ? "Chapter indexing incomplete"
                                      : "Indexing chapters"}
                            </p>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                                {progressLine(job)}
                            </p>
                        </div>

                        {!isActive(job) && (
                            <button
                                onClick={() => void dismiss(job.id)}
                                aria-label="Dismiss"
                                className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            >
                                <X
                                    className="h-3.5 w-3.5"
                                    aria-hidden="true"
                                />
                            </button>
                        )}
                    </div>
                </div>
            ))}
        </>
    );
}
